// src/Medicalcase/components/Casecard/Casecard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Input, Modal, Spin } from "antd";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createCaseComment, fetchCaseComments, toggleCaseLike } from "../../redux/thunks/medicalCasesThunks";
import { CASE_DISCUSSIONS_ROUTES } from "../../routePaths";
import useDoctorInformation from "../../../ProfileDoctor/hooks/useDoctorInformation";
import { buildDoctorDisplayData } from "./doctorDisplay";
import "./Casecard.css";

const IClock = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const IComment = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
  </svg>
);

const ISend = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M16.6915026,12.4744748 L3.50612381,13.2599618 C3.19218622,13.2599618 3.03521743,13.4170592 3.03521743,13.5741566 L1.15159189,20.0151496 C0.8376543,20.8006365 0.99,21.89 1.77946707,22.52 C2.40,22.99 3.50612381,23.1 4.13399899,22.8429026 L21.714504,14.0454487 C22.6563168,13.5741566 23.1272231,12.6315722 22.9702544,11.6889879 L4.13399899,1.16201128 C3.34915502,0.9 2.40734225,1.00636533 1.77946707,1.4776575 C0.994623095,2.10604706 0.837654326,3.0486314 1.15159189,3.99721575 L3.03521743,10.4382088 C3.03521743,10.5953061 3.19218622,10.7524035 3.50612381,10.7524035 L16.6915026,11.5378905 C16.6915026,11.5378905 17.1624089,11.5378905 17.1624089,12.0091827 C17.1624089,12.4744748 16.6915026,12.4744748 16.6915026,12.4744748 Z" />
  </svg>
);

const ILivePlay = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mcd-live-icon lucide lucide-radio-icon lucide-radio"
  >
    <path d="M16.247 7.761a6 6 0 0 1 0 8.478" />
    <path d="M19.075 4.933a10 10 0 0 1 0 14.134" />
    <path d="M4.925 19.067a10 10 0 0 1 0-14.134" />
    <path d="M7.753 16.239a6 6 0 0 1 0-8.478" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

export default function CaseCard({
  caseItem,
  onClick,
  isSaved = false,
  onToggleSave,
  isSaving = false,
  canManage = false,
  canManageLive = false,
  onEditCase,
  onDeleteCase,
  onDeleteLive,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLiveOpen, setDeleteLiveOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingCase, setDeletingCase] = useState(false);
  const [deletingLive, setDeletingLive] = useState(false);
  const [actionError, setActionError] = useState("");
  const [editValues, setEditValues] = useState({ title: "", specialty: "", description: "" });
  const [apiComments, setApiComments] = useState([]);
  const {
    specialty,
    title: cardTitle,
    description: cardDescription,
    author,
    time,
    commentsCount,
    likesCount: initialLikesCount = 0,
    likedByCurrentDoctor = false,
    isLive,
    watchingCount,
    comments = [],
    raw,
  } = caseItem || {};
  const caseId = raw?.id || raw?._id || caseItem?.id || caseItem?._id;
  const liveId = caseItem?.liveId || raw?.liveId || caseItem?.id || caseItem?._id || raw?.id || raw?._id;
  const liveStatus = raw?.status || caseItem?.status || raw?.liveStatus || "live";
  const [liked, setLiked] = useState(Boolean(likedByCurrentDoctor));
  const [likesCount, setLikesCount] = useState(Number(initialLikesCount) || 0);

  const handleSave = async (event) => {
    event.stopPropagation();
    if (!caseId || !onToggleSave || isSaving) return;
    try {
      await onToggleSave("medical_case", caseId);
      setMenuOpen(false);
    } catch {
      // The parent keeps the request error state.
    }
  };

  const title = cardTitle || raw?.caseTitle || raw?.title || t("caseDiscussions.untitledCase", "Untitled case");
  const description = cardDescription || raw?.caseDescription || raw?.description || "";

  const openEdit = (event) => {
    event.stopPropagation();
    setMenuOpen(false);
    setActionError("");
    setEditValues({ title, specialty: specialty || "", description });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const nextTitle = editValues.title.trim();
    const nextSpecialty = editValues.specialty.trim();
    if (!caseId || !nextTitle || !nextSpecialty || savingEdit) return;

    setSavingEdit(true);
    setActionError("");
    try {
      await onEditCase?.(caseId, {
        caseTitle: nextTitle,
        medicalSpeciality: nextSpecialty,
        caseDescription: editValues.description.trim(),
      });
      setEditOpen(false);
    } catch (error) {
      setActionError(error?.message || t("caseDiscussions.editError", "Unable to update this case."));
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (!caseId || deletingCase) return;
    setDeletingCase(true);
    setActionError("");
    try {
      await onDeleteCase?.(caseId);
      setDeleteOpen(false);
    } catch (error) {
      setActionError(error?.message || t("caseDiscussions.deleteError", "Unable to delete this case."));
    } finally {
      setDeletingCase(false);
    }
  };

  const confirmDeleteLive = async () => {
    if (!liveId || !onDeleteLive || deletingLive) return;
    setDeletingLive(true);
    setActionError("");
    try {
      await onDeleteLive(liveId, liveStatus);
      setDeleteLiveOpen(false);
    } catch (error) {
      setActionError(error?.message || t("caseDiscussions.deleteLiveError", "Unable to end this live."));
    } finally {
      setDeletingLive(false);
    }
  };

  const attachments = useMemo(() => {
    const fromRaw = raw?.attachments || raw?.files || raw?.media || [];
    const fromCaseItem = caseItem?.attachments || caseItem?.files || caseItem?.media || [];
    const list = Array.isArray(fromRaw) ? fromRaw : Array.isArray(fromCaseItem) ? fromCaseItem : [];
    return list.filter(Boolean);
  }, [caseItem, raw]);

  const doctorSource = useMemo(() => {
    const source = caseItem?.raw?.doctor || caseItem?.raw?.author || caseItem?.author || {};
    return source;
  }, [caseItem]);

  const { avatarSrc: resolvedAvatarSrc } = useDoctorInformation(doctorSource);
  const doctorDisplay = useMemo(
    () => buildDoctorDisplayData(caseItem, null, resolvedAvatarSrc),
    [caseItem, resolvedAvatarSrc]
  );

  const isValidCaseId = (value) =>
    typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  useEffect(() => {
    setLiked(Boolean(likedByCurrentDoctor));
    setLikesCount(Number(initialLikesCount) || 0);
  }, [caseId, initialLikesCount, likedByCurrentDoctor]);

  useEffect(() => {
    let active = true;

    const loadComments = async () => {
      const caseId = raw?.id || raw?._id || caseItem?.id || caseItem?._id;
      if (!caseId || !showComments) return;

      try {
        const result = await dispatch(fetchCaseComments(caseId));
        const payload = result?.payload;
        const nextComments = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : [];

        if (active) {
          setApiComments(nextComments);
        }
      } catch (err) {
        if (active) {
          setApiComments([]);
        }
      }
    };

    loadComments();
    return () => {
      active = false;
    };
  }, [dispatch, raw, caseItem, showComments]);

  const renderedComments = useMemo(() => {
    const baseComments = apiComments.length > 0 ? apiComments : comments;
    return (baseComments || []).map((comment, idx) => {
      const author = comment?.author || comment?.user || {};
      const authorName = author?.fullName || author?.name || t("caseDiscussions.defaultUser", "User");
      const initials = (authorName.split(" ").map((part) => part?.[0]?.toUpperCase() || "").slice(0, 2).join("") || "U").toUpperCase();
      const text = comment?.content || comment?.text || "";
      const timeLabel = comment?.createdAt ? new Date(comment.createdAt).toLocaleString() : comment?.time || "";

      return {
        id: comment?.id || comment?._id || idx,
        author: {
          name: authorName,
          initials,
          color: author?.color || ["#c7d2fe", "#bbf7d0", "#fecdd3", "#fde68a"][idx % 4],
        },
        text,
        time: timeLabel,
      };
    });
  }, [apiComments, comments, t]);

  const handleLikeToggle = async (e) => {
    e.stopPropagation();
    const caseId = raw?.id || raw?._id || caseItem?.id || caseItem?._id;
    if (!caseId || !isValidCaseId(String(caseId))) return;

    try {
      const result = await dispatch(toggleCaseLike(caseId)).unwrap();
      const nextLiked = Boolean(result?.liked);
      setLiked(nextLiked);
      setLikesCount((previousCount) =>
        Math.max(0, previousCount + (nextLiked ? 1 : -1))
      );
    } catch (err) {
      console.error("Failed to toggle like", err);
    }
  };

  const handleCommentToggle = (e) => {
    e.stopPropagation();
    if (!caseId) return;
    navigate(CASE_DISCUSSIONS_ROUTES.DETAIL(caseId), {
      state: { caseItem },
    });
  };

  const isUuid = (value) =>
    typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  const handleOpenLiveRoom = (e) => {
    e.stopPropagation();

    const liveId =
      caseItem?.liveId ||
      caseItem?.id ||
      caseItem?._id ||
      raw?.liveId ||
      raw?.id ||
      raw?._id;

    if (!liveId) return;

    const liveIdString = String(liveId);
    const streamData = {
      id: liveIdString,
      title,
      description,
      specialty,
      isHost: false,
      watchingCount: Number(watchingCount) || 0,
      startedAt: raw?.createdAt || caseItem?.startedAt || new Date().toISOString(),
      sourceCaseId: liveIdString,
    };

    try {
      sessionStorage.setItem(`medilink_live_stream_${liveIdString}`, JSON.stringify(streamData));
    } catch {
      // Keep navigation working even if session storage is unavailable.
    }

    navigate(CASE_DISCUSSIONS_ROUTES.LIVE(liveIdString), {
      state: { streamData, caseItem },
    });
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    const text = e.currentTarget.comment.value.trim();
    const caseId = raw?.id || raw?._id || caseItem?.id || caseItem?._id;

    if (!text || !caseId) {
      return;
    }

    try {
      setLoading(true);
      await dispatch(createCaseComment({ caseId, content: text }));
      const result = await dispatch(fetchCaseComments(caseId));
      const payload = result?.payload;
      const existing = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : [];

      setApiComments(existing);
      e.currentTarget.reset();
    } catch (err) {
      console.error("Failed to add comment", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <article
      className={`mcd-case ${isLive ? "mcd-case--live" : ""}`}
      onClick={() => onClick?.(caseItem)}
      role="button"
      tabIndex={0}
    >
      <Modal
        title={t("caseDiscussions.editCase", "Edit case")}
        open={editOpen}
        onCancel={() => !savingEdit && setEditOpen(false)}
        onOk={saveEdit}
        confirmLoading={savingEdit}
        okText={t("common.save", "Save")}
        cancelText={t("common.cancel", "Cancel")}
        destroyOnClose
      >
        <label className="mcd-case__edit-label">
          {t("caseDiscussions.caseTitle", "Title")}
          <Input
            value={editValues.title}
            maxLength={180}
            onChange={(event) => setEditValues((current) => ({ ...current, title: event.target.value }))}
          />
        </label>
        <label className="mcd-case__edit-label">
          {t("caseDiscussions.specialty", "Specialty")}
          <Input
            value={editValues.specialty}
            maxLength={120}
            onChange={(event) => setEditValues((current) => ({ ...current, specialty: event.target.value }))}
          />
        </label>
        <label className="mcd-case__edit-label">
          {t("caseDiscussions.description", "Description")}
          <Input.TextArea
            value={editValues.description}
            maxLength={20000}
            rows={5}
            onChange={(event) => setEditValues((current) => ({ ...current, description: event.target.value }))}
          />
        </label>
        {actionError && <p className="mcd-case__action-error">{actionError}</p>}
      </Modal>

      <Modal
        title={t("caseDiscussions.deleteCase", "Delete case")}
        open={deleteOpen}
        onCancel={() => !deletingCase && setDeleteOpen(false)}
        onOk={confirmDelete}
        confirmLoading={deletingCase}
        okButtonProps={{ danger: true }}
        okText={t("common.delete", "Delete")}
        cancelText={t("common.cancel", "Cancel")}
        destroyOnClose
      >
        <p>{t("caseDiscussions.confirmDeleteCase", "Are you sure you want to delete this case?")}</p>
        {actionError && <p className="mcd-case__action-error">{actionError}</p>}
      </Modal>

      <Modal
        title={t("caseDiscussions.deleteLive", "Delete live")}
        open={deleteLiveOpen}
        onCancel={() => !deletingLive && setDeleteLiveOpen(false)}
        onOk={confirmDeleteLive}
        confirmLoading={deletingLive}
        okButtonProps={{ danger: true }}
        okText={t("common.delete", "Delete")}
        cancelText={t("common.cancel", "Cancel")}
        destroyOnClose
      >
        <p>{t("caseDiscussions.confirmDeleteLive", "This will end the live and remove it from the feed. The case itself will remain.")}</p>
        {actionError && <p className="mcd-case__action-error">{actionError}</p>}
      </Modal>

      {isLive && (
        <div className="mcd-case__live-bar">
          <span
            className="mcd-case__live-tag"
            onClick={handleOpenLiveRoom}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleOpenLiveRoom(e);
              }
            }}
          >
            <ILivePlay />
            {t("caseDiscussions.joinLiveNow", "JOIN LIVE NOW")}
          </span>
          <span className="mcd-case__watching">
            <span className="mcd-case__dot" />
            {watchingCount} {t("caseDiscussions.watching", "watching")}
          </span>
        </div>
      )}

      <div className="mcd-case__body">
        <Avatar
          size={36}
          className="mcd-case__avatar"
          src={doctorDisplay.hasAvatar ? doctorDisplay.avatarSrc : undefined}
          style={{ backgroundColor: "#c7d2fe" }}
        >
          {doctorDisplay.initials}
        </Avatar>

        <div className="mcd-case__content">
          <div className="mcd-case__heading-meta">
            <span className="mcd-case__heading-author">{doctorDisplay.name}</span>
            <span className="mcd-case__heading-specialty">
              <span className="mcd-case__heading-separator" aria-hidden="true">
                •
              </span>
              <span className="mcd-case__specialty-tag">{specialty}</span>
            </span>
            {(typeof onToggleSave === "function" || (canManage && (onEditCase || onDeleteCase)) || (isLive && canManageLive && onDeleteLive)) && (
              <div className="mcd-case__menu" onClick={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  className="mcd-case__menu-button"
                  aria-label={t("caseDiscussions.options", "Options")}
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((open) => !open)}
                >
                  •••
                </button>
                {menuOpen && (
                  <div className="mcd-case__menu-list" role="menu">
                    {typeof onToggleSave === "function" && (
                      <button type="button" role="menuitem" onClick={handleSave} disabled={isSaving}>
                        {isSaving
                          ? t("common.loading", "Loading...")
                          : isSaved
                            ? t("caseDiscussions.unsave", "Unsave")
                            : t("caseDiscussions.save", "Save")}
                      </button>
                    )}
                    {canManage && onEditCase && (
                      <button type="button" role="menuitem" onClick={openEdit}>
                        {t("common.edit", "Edit")}
                      </button>
                    )}
                    {canManage && onDeleteCase && (
                      <button
                        type="button"
                        role="menuitem"
                        className="mcd-case__menu-item--danger"
                        onClick={(event) => {
                          event.stopPropagation();
                          setMenuOpen(false);
                          setActionError("");
                          setDeleteOpen(true);
                        }}
                      >
                        {t("common.delete", "Delete")}
                      </button>
                    )}
                    {isLive && canManageLive && onDeleteLive && (
                      <button
                        type="button"
                        role="menuitem"
                        className="mcd-case__menu-item--danger"
                        onClick={(event) => {
                          event.stopPropagation();
                          setMenuOpen(false);
                          setActionError("");
                          setDeleteLiveOpen(true);
                        }}
                      >
                        {t("caseDiscussions.deleteLive", "Delete live")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <h3 className="mcd-case__title">{title}</h3>
          <p className="mcd-case__desc">{description}</p>

          {attachments.length > 0 ? (
            <div className="mcd-case__attachments">
              {attachments.map((attachment, index) => {
                const href = attachment?.url || attachment?.fileUrl || attachment?.filePath || attachment?.path || attachment?.src || null;
                const label =
                  attachment?.name ||
                  attachment?.fileName ||
                  `${t("caseDiscussions.attachment", "Attachment")} ${index + 1}`;
                if (!href) return null;
                const mimeType = attachment?.mimeType || attachment?.contentType || attachment?.type || "";
                const isImage = mimeType.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|tiff?|svg)$/i.test(href);
                const isVideo = mimeType.startsWith("video/") || /\.(mp4|mov|webm|avi)$/i.test(href);
                return (
                  <a
                    key={`${label}-${index}`}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className={`mcd-case__attachment ${isImage || isVideo ? "mcd-case__attachment--preview" : ""}`}
                    onClick={(e) => e.stopPropagation()}
                    title={label}
                  >
                    {isImage ? (
                      <img className="mcd-case__attachment-thumb" src={href} alt={label} loading="lazy" />
                    ) : isVideo ? (
                      <video className="mcd-case__attachment-video" src={href} muted preload="metadata" />
                    ) : (
                      <span className="mcd-case__attachment-file-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                          <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                        </svg>
                      </span>
                    )}
                    <span className="mcd-case__attachment-label">{label}</span>
                  </a>
                );
              })}
            </div>
          ) : null}

          <div className="mcd-case__footer">
            <span className="mcd-case__author">
              <Avatar
                size={18}
                src={doctorDisplay.hasAvatar ? doctorDisplay.avatarSrc : undefined}
                style={{ backgroundColor: "#c7d2fe" }}
              >
                {doctorDisplay.initials}
              </Avatar>
              {doctorDisplay.name}
            </span>
            <span className="mcd-case__time">
              <IClock />
              {time}
            </span>
            <span
              className={`mcd-case__comments ${liked ? "mcd-case__comments--active" : ""}`}
              onClick={handleLikeToggle}
              role="button"
              tabIndex={0}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill={liked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8">
                <path d="M12 21s-6.5-4.35-8.2-8.2C2.3 9.7 3.2 6.5 6 5.4c1.5-.6 3.2-.2 4.5 1 1.3-1.2 3-1.6 4.5-1 2.8 1.1 3.7 4.3 2.2 7.4C18.5 16.65 12 21 12 21Z" />
              </svg>
              {likesCount} {t("caseDiscussions.like", "Like")}
            </span>
            <span
              className="mcd-case__comments"
              onClick={handleCommentToggle}
              role="button"
              tabIndex={0}
            >
              <IComment />
              {commentsCount} {t("caseDiscussions.comments", "comments")}
            </span>
          </div>

          {(showComments || loading) && (
            <div className={`mcd-case__comments-section ${showComments ? "mcd-case__comments-section--open" : ""}`}>
              {showComments && (
                <>
                  {/* Composer - Formulaire d'ajout de commentaire */}
                  <div className="mcd-comments-composer">
                    <form className="mcd-composer-form" onSubmit={handleAddComment}>
                      <textarea
                        name="comment"
                        placeholder={t("homePage.writeComment", "Write a comment...")}
                        rows={1}
                        onInput={(e) => {
                          e.target.style.height = "auto";
                          e.target.style.height = `${e.target.scrollHeight}px`;
                        }}
                      />
                      <button
                        type="submit"
                        aria-label={t("caseDiscussions.sendComment", "Send comment")}
                        title={t("caseDiscussions.send", "Send")}
                      >
                        <ISend />
                      </button>
                    </form>
                  </div>

                  {/* Liste des commentaires */}
                  <div className="mcd-comments-list">
                    {loading ? (
                      <div className="mcd-comments-loading">
                        <Spin size="small" />
                      </div>
                    ) : renderedComments.length > 0 ? (
                      renderedComments.map((comment) => (
                        <div key={comment.id} className="mcd-comment-item">
                          <div className="mcd-comment-avatar">
                            <Avatar size={40} style={{ backgroundColor: comment.author.color }}>
                              {comment.author.initials}
                            </Avatar>
                          </div>
                          <div className="mcd-comment-body">
                            <div className="mcd-comment-header">
                              <span className="mcd-comment-author">{comment.author.name}</span>
                              <span className="mcd-comment-time">{comment.time}</span>
                            </div>
                            <p className="mcd-comment-text">{comment.text}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="mcd-comments-empty">{t("caseDiscussions.noComments", "No comments yet")}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}